import { useEffect, useRef, useState } from "react";
import { FileSignature, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { openPdfBlob } from "@/lib/openPdf";

/**
 * PaymentAuthButton
 * Compact action button that opens a small language picker (PT/EN/ES) and
 * downloads the auto-filled Payment Authorization Form PDF for a given vehicle.
 *
 * Props:
 *   vehicleId : string  — required. Backend will auto-fill customer / car / installments.
 *   t         : i18n function
 *   variant   : "compact" (square icon-only · default) | "inline" (icon + label chip)
 *   className : extra classes for the trigger button
 *   stopPropagation : if true, click events don't bubble (use inside clickable cards)
 */
export default function PaymentAuthButton({
  vehicleId,
  t,
  variant = "compact",
  className = "",
  stopPropagation = false,
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  // close on outside click
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const download = async (lang) => {
    setLoading(true);
    setOpen(false);
    try {
      const res = await api.post(
        "/payment-authorization.pdf",
        { vehicle_id: vehicleId, lang },
        { responseType: "blob" },
      );
      openPdfBlob(res.data, "payment-authorization.pdf");
    } catch (e) {
      toast.error(e?.response?.data?.detail || (t && t("error_generic")) || "Erro");
    } finally {
      setLoading(false);
    }
  };

  const langs = [
    { code: "pt", flag: "🇧🇷", name: "Português" },
    { code: "en", flag: "🇺🇸", name: "English" },
    { code: "es", flag: "🇪🇸", name: "Español" },
  ];

  const handleTriggerClick = (e) => {
    if (stopPropagation) e.stopPropagation();
    if (loading) return;
    setOpen((v) => !v);
  };

  const tooltip = (t && t("payment_auth_btn")) || "Autorização de Pagamento";
  const pickLabel = (t && t("payment_auth_pick_lang")) || "Idioma do documento";

  return (
    <div ref={ref} className="relative inline-block">
      {variant === "inline" ? (
        <button
          type="button"
          data-testid={`pay-auth-trigger-${vehicleId}`}
          onClick={handleTriggerClick}
          disabled={loading}
          title={tooltip}
          className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest px-2 py-1 border border-info/50 bg-info/10 text-info hover:bg-info hover:text-white disabled:opacity-50 transition-colors ${className}`}
        >
          {loading ? <Loader2 size={11} className="animate-spin" /> : <FileSignature size={11} />}
          {tooltip}
        </button>
      ) : (
        <button
          type="button"
          data-testid={`pay-auth-trigger-${vehicleId}`}
          onClick={handleTriggerClick}
          disabled={loading}
          title={tooltip}
          className={`w-9 h-9 border border-info/40 text-info hover:bg-info hover:text-white disabled:opacity-50 flex items-center justify-center transition-colors ${className}`}
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <FileSignature size={14} />}
        </button>
      )}

      {open && !loading && (
        <div
          data-testid={`pay-auth-menu-${vehicleId}`}
          onMouseDown={(e) => stopPropagation && e.stopPropagation()}
          className="absolute right-0 z-30 mt-1 w-44 bg-background border border-border shadow-xl"
        >
          <p className="label-eyebrow px-3 py-2 border-b border-border text-info">
            {pickLabel}
          </p>
          {langs.map((l) => (
            <button
              key={l.code}
              type="button"
              data-testid={`pay-auth-lang-${l.code}-${vehicleId}`}
              onClick={(e) => { if (stopPropagation) e.stopPropagation(); download(l.code); }}
              className="w-full px-3 py-2 text-left text-xs flex items-center gap-2 hover:bg-info/10 hover:text-info transition-colors"
            >
              <span className="text-base leading-none">{l.flag}</span>
              <span className="font-display font-bold uppercase tracking-widest text-[10px]">
                {l.name}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
